import { useState, useEffect } from "react";
import { Package, Clock, Mail, MessageSquare } from "lucide-react";
import { StatsCard } from "@b2b/ui-components";
import { sellerService } from "@b2b/services";

export const KeyMetrics = () => {
  const [data, setData] = useState<{
    approvedProducts: number;
    pendingProducts: number;
    enquiriesTotal: number;
    pendingEnquiries: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError(null);

        const [productStatsRes, enquiriesRes, pendingEnquiriesRes] =
          await Promise.all([
            sellerService.getProductStatistics(),
            sellerService.getStorefrontInquiries(undefined, 1, 1),
            sellerService.getStorefrontInquiries("pending", 1, 1),
          ]);

        const stats = productStatsRes?.success ? productStatsRes.data : null;

        setData({
          approvedProducts: stats?.byStatus?.approved ?? 0,
          pendingProducts: stats?.byStatus?.pending ?? 0,
          enquiriesTotal: enquiriesRes?.total ?? 0,
          pendingEnquiries: pendingEnquiriesRes?.total ?? 0,
        });
      } catch (err: unknown) {
        console.error("Error fetching seller dashboard metrics:", err);
        setError(err instanceof Error ? err.message : "Failed to load metrics");
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-800 text-sm">
        {error || "Failed to load overview"}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
      <StatsCard
        title="Approved Products"
        value={String(data.approvedProducts)}
        change={
          data.approvedProducts > 0
            ? "Live on your storefront"
            : "Add products to get started"
        }
        changeType="neutral"
        icon={Package}
        variant="primary"
      />
      <StatsCard
        title="Pending Approval"
        value={String(data.pendingProducts)}
        change={
          data.pendingProducts > 0
            ? "Awaiting admin review"
            : "No products in review"
        }
        changeType="neutral"
        icon={Clock}
      />
      <StatsCard
        title="Buyer Enquiries"
        value={String(data.enquiriesTotal)}
        change={
          data.enquiriesTotal > 0
            ? "Received from buyers"
            : "Enquiries appear here"
        }
        changeType="neutral"
        icon={Mail}
      />
      <StatsCard
        title="Awaiting Reply"
        value={String(data.pendingEnquiries)}
        changeType="neutral"
        icon={MessageSquare}
      />
    </div>
  );
};
